<script lang="ts" setup>
    import _, { keyBy } from 'lodash';
    import { computed, onMounted, watch, ref, reactive} from 'vue';
    import { TableRowType,TableRowHighlight, AlignmentTypeStyle, PaginationButtonSizeStyle } from '../Enums/UiEnums'
    import { ICol, FormattedValueStr, RenderModeType, CellStyles,IRowSelection } from '../lib/ChoiceTableHelpers';
    import { GetCheckboxEleValue, GetEleValue } from '../lib/EventUtils';
    import * as Notification from '../lib/Notification';
    import * as Export from '../lib/Export';
    import { ICssLength, CssLength, CssLayoutUnits } from '../lib/CssHelpers';
    import ChoiceTableCell from '../components/Table/ChoiceTableCell.vue'
    import PaginationContainer from './Pagination/PaginationContainer.vue';
    import PaginationButtons from './Pagination/PaginationButtons.vue';

    enum ChoiceTableClickType {
        rowclick = "rowclick",
        rowdblclick = "rowdblclick"
    }
    interface ISortDir{
        ascendingSortOrder: boolean
    }
    
    const props = withDefaults(defineProps<{
            columns: ICol[],
            data?: Record<string, any>[] ,   // Table body values
            footerVals?: Record<string, any> | Record<string, any>[],   // Table footer values
            showHeader?: boolean,
            showFooter?: boolean,
            showFilters?: boolean,
            allowSorting?: boolean,
            checkBoxScale?: number,
            screenReaderCaption?: string,
            keyValueDataMember?: string,    // The name of a value in data which is a unique key for the row
            height?: ICssLength|number|Object,      // Plain number is pixels
            width?: ICssLength|number|Object,      // Plain number is pixels
            fontSize?: "smaller"|"small"|"normal"|"large"|"larger",
            rowHighlight? : TableRowHighlight,
            rowBodyClass?: (row: Record<string, any>) => string | undefined,
            itemsPerPage?: number,
            currentTopItem?: number,
            alignStyle?: AlignmentTypeStyle,
            buttonSize?: PaginationButtonSizeStyle,
            numberMaxPagesToDisplay?: number,
            showPaginationButtonsAboveChoiceTable?: boolean,
            showPaginationButtonsBelowChoiceTable?: boolean,
        }>(), {
            showHeader: true,
            showFooter: false,
            showFilters: false,
            allowSorting: false,
            checkBoxScale: 1.5, // Per Paul's choice...
            rowHighlight : TableRowHighlight.NoHighLight,
            showPaginationButtonsAboveChoiceTable: false,
            showPaginationButtonsBelowChoiceTable: false,
    });

    const emit = defineEmits<{
        (e: "rowclick", rowData : IRowSelection, rowType: TableRowType, evt: MouseEvent): void,
        (e: "rowdblclick", rowData : IRowSelection, rowType: TableRowType, evt: MouseEvent): void,
        (e: "cellclick", colData: ICol, row : IRowSelection, rowType: TableRowType, evt: MouseEvent): void,
        (e: "celldblclick", colData: ICol, row : IRowSelection, rowType: TableRowType, evt: MouseEvent): void,
        (e: "selectChanged", checked: boolean|undefined, colData: ICol, row?: IRowSelection): void,
        (e: "update:currentTopItem", currentTopItem: number): void
    }>();

    // Methods available to external users
    defineExpose({exportToExcel, convertToPdf, clearSelectedRow, getBoundingClientRect, clearFilterValues, setSelectedRow});


    //const headerSelectionValue = ref<boolean>();
    const rowTableKey = ref<string>("");

    onMounted(() =>{
        AssertConfigIssues();
        rowTableKey.value = "";

        // Initialize the header selection value
        OnChangedSelectionInputValue();
    });
    const choiceTableRef = ref<HTMLElement | undefined>();

    const compData = reactive<{
        currentTopItem?: number
    }>({
        currentTopItem: 0
    });

     

    // Watch for changes to data or columns 
    watch(() => props.data, (_newVal, _oldVal) => OnChangedSelectionInputValue());    
    watch(() => props.columns, (_newVal, _oldVal) => {
        OnChangedSelectionInputValue();
        
        // Reset the filter data
        columnFilterData.value = [];
    });
    watch(() => props.keyValueDataMember, async () =>{
        AssertConfigIssues();
    });
    watch(() => props.currentTopItem, (newVal, _oldVal) => compData.currentTopItem = newVal);

    // Look for configuration issues
    async function AssertConfigIssues()
    {
        if (undefined === props.keyValueDataMember && props.allowSorting)
            await Notification.popDevelopmentAlert("There is no <b>keyValueDataMember</b> value specified but <b>allowSorting</b> is <b class='text-primary'>true</b>.<hr/>We need the key to ensure rows are properly updated if sorting changes the display order which can lead to mis-draws.", "Choice Table Issue");
    }

    // Get the cell styles -- apply column def styles if not provided by the supplied style function
    function CellStyle(col: ICol, row: any, rowType: TableRowType): Record<string, string>
    {
        let style : Record<string, string> = {};
        if (col.StyleDel)
            style = col.StyleDel(col, row, rowType) ?? {};
        else
        {
            // Default styles based on RenderMode if StyleDel was not specified for column
            if (RenderModeType.AccountingMoney === col.RenderMode)
            {
                const cellStyle = CellStyles.NegativeValuesRedTextStyle(col, row, rowType);
                if (cellStyle)
                    style = cellStyle;
            }
        }

        // Add alignment?
        if (!style["text-align"] && col.Alignment)
        {
            style["text-align"] = col.Alignment;
        }
        if (!style["cursor"] && rowType === TableRowType.Header && props.allowSorting)
        {
            style["cursor"] = "pointer";
        }

        return style;
    }

    // Get the cell classes
    function CellClass(col: ICol, row: any, rowType: TableRowType): string[] | undefined
    {
        let highlightClass = "";
        if ((rowType === TableRowType.Body) && (rowTableKey.value !== "") && (props.rowHighlight !== TableRowHighlight.NoHighLight) && (props.keyValueDataMember)) {
            const rowKey = row[props.keyValueDataMember].toString() ?? "";
            if (rowKey === rowTableKey.value) {
                if ((props.rowHighlight === TableRowHighlight.onSingleClick) || (props.rowHighlight === TableRowHighlight.onDoubleClick)) {
                    highlightClass = "selected-row";
                }
                else {
                    highlightClass = "selected-row-border";
                }
            }
        }

        if (!col.ClassDel) {
            if (highlightClass) {
                return [highlightClass];
            }
            else {
                return;
            }
        }

        const cell_return_class = col.ClassDel(col, row, rowType);
        if (highlightClass) {
            cell_return_class?.push(highlightClass);
        }
        return cell_return_class;
    }

    // Update the rows with the new header check mark value
    function HeaderSelectionChanged(col: ICol, newValEvent: Event){
        const dataField = col.DataField;
        if (!dataField)
            return;

        // Apply the new state to the visible rows?
        const newCheckboxValue = GetCheckboxEleValue(newValEvent);
        visibleRowData.value?.forEach(row => row[dataField] = newCheckboxValue);

        emit("selectChanged", newCheckboxValue, col, undefined);
    }

    function ShowFilterTextBox(col: ICol):boolean
    {
        return !(RenderModeType.Selection === col.RenderMode || (col.HideFilter ?? false));
    }
    const checkboxScaleStyle = computed(() =>{
        return "scale: " + props.checkBoxScale.toString() + ";";
    });
    const filterData = computed(() =>{
        _filterData.value = {};
        for(const col of props.columns)
        {
            if (col.DataField)
                _filterData.value[col.DataField] = "";
        }
        return _filterData.value;
    });
    const _filterData = ref<Record<string, string>>({});

    function clearFilterValues()
    {
        for(let filterVal in filterData.value)
        {
            filterData.value[filterVal] = "";
        }
        columnFilterData.value = [];
    }

    // The data used to generate the table rows
    const visibleRowData = computed(() =>{
        const sortCols = sortColumns.value.filter(col => false !== col.col.Sort);
        const iteratees = sortCols.map(col => col.col.Sort ?? col.col.DataField);
        const sortOrder = sortCols.map(col => col.ascending ? "asc" : "desc");

        return _.orderBy(props.data?.filter(IsRowVisible), iteratees, sortOrder);
    });

    // Is this row of data visible?
    function IsRowVisible(row: Record<string, any>){
        if (!columnFilterData.value.length)
            return true;

        for(const filter of columnFilterData.value.filter(filterTxt => filterTxt))
        {
            if (!IsFilterMatch(filter, row))
                return false;
        }
        return true;
    }

    // Get the CSS class for the sort icon
    function SortIconClass(col: ICol) : string | undefined
    {
        const matchingSortCol = MatchingSortCol(col);
        if (true === matchingSortCol?.ascending)
            return "fa-solid fa-angle-up";
        if (false === matchingSortCol?.ascending)
            return "fa-solid fa-angle-down";
    }

    // Get the value we will filter by
    function FilterValue(filter: IColFilter, row: Record<string, any>) : string | undefined | null
    {
        if (!filter.col.FilterValue)
        {
            return FormattedValueStr(filter.col, row, TableRowType.Body);
        }
        
        // Is this a formatted value?
        const value = (undefined === filter.col.DataField) ? undefined : row[filter.col.DataField];
        const filterValue = filter.col.FilterValue(value, filter.col, row, TableRowType.Body);
        return filterValue;
    }

    // Should the row be show based on the specified filter settings?
    function IsFilterMatch(filter: IColFilter, row: Record<string, any>): boolean
    {
        // Is this a formatted value?
        const filterValue = FilterValue(filter, row);
        if (filterValue)
        {
            // Make sure we have a lower case string
            const lowerCaseValue = ("string" === typeof filterValue) ? filterValue.toLowerCase() : "" + filterValue;

            if (lowerCaseValue.includes(filter.lowerCaseFilterText))
                return true;

            if (filter.justNumbersFilterText && filter.justNumbersFilterText.length > 0)
            {
                const valueAsJustNumbers = lowerCaseValue.replace(/\D/g,'');
                if (valueAsJustNumbers.includes(filter.justNumbersFilterText))
                    return true;
            }
        }

        // Is there a value in the table for this data field? (Convert NULL field value to undefined)
        const fieldValue = filter.col.DataField ? (row[filter.col.DataField] ?? undefined) : undefined;

        // No value and no filter?
        if (undefined === fieldValue && "" === filter.filterText)
        {
            // Show the row
            return true;
        }

        // Data type filters
        const fieldValueType = typeof fieldValue;
        if ("string" === fieldValueType)
        {
            if (fieldValue.toLowerCase().includes(filter.lowerCaseFilterText))
                return true;
        }
        if ("number" === fieldValueType && filter.justNumbersFilterText && filter.justNumbersFilterText.length > 0)
        {
            if (fieldValue.toString().includes(filter.justNumbersFilterText))
                return true;
        }

        return false;        
    }

    // Make sure to keep the header synced up with the row selections
    function OnChangedSelectionInputValue()
    { 
        const cols = props.columns.filter(oCol => RenderModeType.Selection === oCol.RenderMode);
        if (!cols)
            return;

        cols.forEach((col) => {
            const dataField = col.DataField;
            if (!dataField)
            {
                console.log(`OnChangedSelectionInputValue: DataField not valid`);
                return;
            }
            // Is there any unchecked row?  If so uncheck header 'selection' checkbox
            const hasUnChecked = visibleRowData.value?.find(row => true !== row[dataField]);
            col.HeaderSelectionValue = !hasUnChecked;
        });
    }

    function UpdatedFilter(colIndex: number, col:ICol, evt: Event){
        const filterText = GetEleValue(evt);
        const valueWithoutNumericFormatting = filterText.replace(/[$,]]/g,'');
        const justNumbersFilterText = valueWithoutNumericFormatting.match(/\D/g)?.length ? undefined : valueWithoutNumericFormatting;

        columnFilterData.value[colIndex] = {col, filterText, lowerCaseFilterText: filterText.toLowerCase(), justNumbersFilterText };
    }
    type IColFilter = {col:ICol, filterText: string, lowerCaseFilterText: string, justNumbersFilterText?: string};
    const columnFilterData = ref<IColFilter[]>([]);

    // Should the footer be displayed?
    const ShowFooter = computed(() =>{
        if (true === props.showFooter)
            return true;
        if (undefined === props.showFooter)
            return undefined !== props.footerVals;
        return false;
    });

    // Association between a column and the sort order
    interface ISortColInfo {
        col: ICol,
        ascending: boolean
    }
    const sortColumns = ref<ISortColInfo[]>([]);

    // Get the matching sort column
    function MatchingSortCol(col: ICol)
    {
        return sortColumns.value.find(sortCol => _.isEqual(col, sortCol.col));
    }

    function onClickHeaderColumn(col: ICol, evt: MouseEvent)
    {
        if (!props.allowSorting)
            return;

        // On a selection column checkbox, prevent sorting
        if (RenderModeType.Selection === col.RenderMode)
        {
            if ((evt.target as HTMLInputElement)?.type === "checkbox")
            {
                return;
            }
        }

        if ("boolean" === typeof col.Sort)
        {
            if (false === col.Sort)
                return;
        }

        let matchingSortCol = MatchingSortCol(col);
        if (!matchingSortCol)
        {
            const newSortCol:ISortColInfo = { col, ascending: col.InitialSortAscending ?? true};

            // If the ctrl key is down, add a sort column
            if (true === evt.ctrlKey)
            {
                sortColumns.value.push(newSortCol);
                return;
            }
            else
            {
                sortColumns.value =[newSortCol];
            }
        }

        if (matchingSortCol)
        {
            // Invert search order if currently sorting by the column
            if (true !== evt.ctrlKey)
                sortColumns.value =[matchingSortCol];

            matchingSortCol.ascending = !matchingSortCol.ascending;
        }
    }

    // Export the data to an Excel file
    async function exportToExcel(fileName: string, sheetName: string) {
        if (!props.data || !props.columns)
            return;

        let exportData = [];

        // Export the header
        if (props.showHeader)
        {
            let exportRow = [];
            for(const col of props.columns)
            {
                exportRow.push(col.Name);
            }
            exportData.push(exportRow);
        }

        // Export the table body
        for(const row of props.data)
        {
            let exportRow = [];
            for(const col of props.columns)
            {
                const formattedValue = FormattedValueStr(col, row, TableRowType.Body);
                exportRow.push(formattedValue);
            }
            exportData.push(exportRow);
        }

        if (props.showFooter && props.footerVals)
        {
            let exportRow = [];
            for(const col of props.columns)
            {
                for(const footerRow of footerRows.value)
                {
                    const formattedValue = FormattedValueStr(col, footerRow, TableRowType.Footer);
                    exportRow.push(formattedValue);
                }
            }
            exportData.push(exportRow);            
        }

        if (-1 === fileName.indexOf("."))
            fileName += ".xlsx";

        Export.ExportSheetToExcel(exportData, fileName, sheetName);
    }

    async function convertToPdf(fileName:string)
    {
        if ("undefined" === typeof(jsPDF))
        {
            alert("Cannot convert table to PDF.  jsPDF not available.");
            return;
        }
        if ("undefined" == typeof(autoTable))
        {
            alert("Cannot convert table to PDF.  autoTable not available.");
            return;
        }

        const doc = new jsPDF({
            orientation: "portrait", 
            unit: "pt", 
            format: "letter"});

        if (!choiceTableRef.value)
            return;

        autoTable(doc, { html: choiceTableRef.value });
        doc.save(fileName ?? "Table.pdf");
    }

    const tableStyle = computed(() =>{
        const parts: string[] = [];
        if (props.height)
        {
            if ("number" === typeof(props.height))
                parts.push("height: " + new CssLength(props.height, CssLayoutUnits.px).cssValue);
            else if (CssLength.hasCssLength(props.height))
            {
                const height = props.height as ICssLength;
                parts.push("height: " + new CssLength(height.amount, height.units).cssValue);
            }

            parts.push("overflow-y: scroll");
        }
        if (props.width)
        {
            if ("number" === typeof(props.width))
                parts.push("width: " + new CssLength(props.width, CssLayoutUnits.px).cssValue);
            else if (CssLength.hasCssLength(props.width))
            {
                const width = props.width as ICssLength;
                parts.push("width: " + new CssLength(width.amount, width.units).cssValue);
            }
        }

        switch(props.fontSize)
        {
            case "smaller":
                parts.push("font-size: 0.75rem");
                break;
            case "small":
                parts.push("font-size: 0.85rem");
                break;
            case "large":
                parts.push("font-size: 1.15rem");
                break;
            case "larger":
                parts.push("font-size: 1.25rem");
                break;
        }
        return parts;
    });

    function choiceTableRowClickEvent(rowClickType: ChoiceTableClickType, row: Record<string, any>, rowType: TableRowType, _evt: MouseEvent) : void { 

        if (
            (((props.rowHighlight === TableRowHighlight.onSingleClick) || (props.rowHighlight === TableRowHighlight.onSingleClickBorder)) && (rowClickType == ChoiceTableClickType.rowclick)) ||
            (((props.rowHighlight === TableRowHighlight.onDoubleClick) || (props.rowHighlight === TableRowHighlight.onDoubleClickBorder)) && (rowClickType == ChoiceTableClickType.rowdblclick))
           ) {  
            if (props.keyValueDataMember)
                rowTableKey.value = row[props.keyValueDataMember].toString() ?? "";
        } 
        if (rowClickType == ChoiceTableClickType.rowdblclick) {
            emit("rowdblclick", row, rowType, _evt);
            return;
        }
        if (rowClickType == ChoiceTableClickType.rowclick) {
            emit("rowclick", row, rowType, _evt);
        }
    }

    function EmitSelectionChange(col: ICol, row: Record<string, any>)
    {
        const checked = col.DataField ? row[col.DataField] : undefined;
        emit("selectChanged", checked, col, row);
    }

    function clearSelectedRow() {
        rowTableKey.value = "";
    }

    function setSelectedRow(selectedTableKey: string) {
        rowTableKey.value = selectedTableKey;
    }

    function getBoundingClientRect(): DOMRect|undefined
    {
        return choiceTableRef.value?.getBoundingClientRect();
    }

    const footerRows = computed(() => {
        if (Array.isArray(props.footerVals))
            return props.footerVals;
        return [props.footerVals];
    });

    const rowTopBorder = "border-top: 2px solid darkblue";

    const usePagination = computed<boolean>(() => (props.itemsPerPage ?? 0) > 0);

</script>

<style scoped>
    THEAD, THEAD > tr > th {
        background-color: #c3daf9;
    }

    TFOOT, TFOOT > tr > th {
        background-color: #c3daf9;
    } 
    
    td.selected-row-border {
        border-top: 2px double #6495ed;
        border-bottom: 2px double #6495ed; 
    }
    tr td.selected-row-border:first-child{ 
        border-left: 2px double #6495ed; 
    }
    tr td.selected-row-border:last-child { 
        border-right: 2px double #6495ed; 
    }
    .selected-row {
        background-color: #afd2ff;      
    }
    @media screen
    {
        .TableCaptionClass{
            display: none;
        }
    }

    @media print
    {
        .TableCaptionClass{
            display: none;
        }
    }


    @media speech {
        .TableCaptionClass{
            display: revert;
        }
    }

</style>



<template>
    <!-- Pagination is only used if you pass in props?.itemsPerPage (and if data length is greater than props?.itemsPerPage)-->
    <PaginationButtons 
        v-if="props.showPaginationButtonsAboveChoiceTable && usePagination && (props.data?.length ?? 0) > (props?.itemsPerPage ?? 0)"
        :totalItems="props.data?.length ?? 0" 
        :buttonSize="props.buttonSize" 
        :alignStyle="props.alignStyle" 
        :itemsPerPage="props.itemsPerPage" 
        :currentTopItem="(props.data?.length ?? 0) < (compData.currentTopItem ?? 0) ? 0 : compData.currentTopItem ?? 0" 
        @click="(currentTopItem, _currentPage ) => {compData.currentTopItem = currentTopItem; emit('update:currentTopItem', currentTopItem)}" />

    <div :style="tableStyle">
        <table class="table table-bordered table-striped table-sm table-hover" ref="choiceTableRef">
            <caption class="TableCaptionClass">{{props.screenReaderCaption}}</caption>
            <thead class="TableHead" v-if="showHeader">
                <tr style="border-top: 2px solid darkblue; border-bottom: 2px solid cornflowerblue">
                    <th v-for="col in props.columns" 
                            :style="CellStyle(col, [], TableRowType.Header)" 
                            :class="CellClass(col, [], TableRowType.Header)"
                            @click="(evt) => onClickHeaderColumn(col, evt)">
                        <slot name="header" :col="col">
                            <template v-if="RenderModeType.Selection === col.RenderMode">
                                <input type="checkbox" v-model="col.HeaderSelectionValue" v-if="col.DataField" :style="checkboxScaleStyle"  
                                @change="(evt) => HeaderSelectionChanged(col, evt)"/>
                            </template>
                            {{col.Name}}
                            <em v-if="SortIconClass(col)" :class="SortIconClass(col)"></em>
                        </slot>
                    </th>
                </tr>
            </thead>
            <thead v-if="showFilters">
                <tr style="border-top: 2px solid darkblue; border-bottom: 2px solid cornflowerblue">
                    <th v-for="(col, colIndex) in props.columns" :style="CellStyle(col, [], TableRowType.Header)" :class="CellClass(col, [], TableRowType.Header)">
                        <slot name="header" :col="col">
                            <template v-if="ShowFilterTextBox(col)">
                                <input v-model="filterData[col.DataField ?? '_ignore']" class="form-control" @input="(evt) => UpdatedFilter(colIndex, col, evt)" />
                            </template>
                        </slot>
                    </th>
                </tr>            
            </thead>
            <tbody v-if="props.data">
                <PaginationContainer 
                    :data="visibleRowData"
                    :itemsPerPage="props.itemsPerPage"
                    :currentTopItem="(props.data?.length ?? 0) < (compData?.currentTopItem ?? 0) ? 0 : compData.currentTopItem"
                    :alignStyle="props.alignStyle"
                    :buttonSize="props.buttonSize"
                    :numberMaxPagesToDisplay="props.numberMaxPagesToDisplay"
                    @update:currentTopItem="currentTopItem => {compData.currentTopItem = currentTopItem; emit('update:currentTopItem', currentTopItem)}"
                >
                    <template #header>
                        <template></template>
                    </template>
                    <template #body="{ visibleItems }">
                        <tr v-for="row in visibleItems" :key="props.keyValueDataMember ? row[props.keyValueDataMember] : null" 
                                :class="props.rowBodyClass ? props.rowBodyClass(row): ''"
                                @click="(evt) => choiceTableRowClickEvent(ChoiceTableClickType.rowclick, row, TableRowType.Body, evt)" 
                                @dblclick="(evt) => choiceTableRowClickEvent(ChoiceTableClickType.rowdblclick, row, TableRowType.Body, evt)" >
                            <template v-for="col in props.columns" >
                                <td :style="CellStyle(col, row, TableRowType.Body)" :class="CellClass(col, row, TableRowType.Body)"  @click="(evt) => emit('cellclick', col, row, TableRowType.Body, evt)" @dblclick="(evt) => emit('celldblclick', col, row, TableRowType.Body, evt)">
                                    <slot name="cell" :col="col" :row="row" :value="col.DataField ? row[col.DataField] : undefined">
                                        <ChoiceTableCell :col="col" :row="row" :checkboxScaleStyle="checkboxScaleStyle" @onChange="EmitSelectionChange(col, row)"/>
                                    </slot>
                                </td>
                            </template>
                        </tr>
                    </template>
                    <template #footer>
                        <template></template>
                    </template>
                </PaginationContainer>            
            </tbody>
            <tfoot v-if="ShowFooter">
                <tr class="TableFoot" v-for="(footerRow, index) in footerRows" :style="0 === index ? rowTopBorder : undefined">
                    <th v-for="col in props.columns" :style="CellStyle(col, footerRow, TableRowType.Footer)" :class="CellClass(col, footerRow, TableRowType.Footer)">
                        <slot name="footer" :col="col" :row="footerRow">
                            {{FormattedValueStr(col, footerRow, TableRowType.Footer)}}
                        </slot>
                    </th>
                </tr>
            </tfoot>
        </table>
    </div>
    <PaginationButtons 
        v-if="props.showPaginationButtonsBelowChoiceTable && usePagination && (props.data?.length ?? 0) > (props?.itemsPerPage ?? 0)"
        :totalItems="props.data?.length ?? 0" 
        :buttonSize="props.buttonSize" 
        :alignStyle="props.alignStyle" 
        :itemsPerPage="props.itemsPerPage" 
        :currentTopItem="(props.data?.length ?? 0) < (compData.currentTopItem ?? 0) ? 0 : compData.currentTopItem ?? 0" 
        @click="(currentTopItem, _currentPage ) => {compData.currentTopItem = currentTopItem; emit('update:currentTopItem', currentTopItem)}" />

</template>